import { ActionType, createAction } from "typesafe-actions";

import type {
  Activity,
  AnyEntityId,
  EntityTypesSupportedByActivities,
} from "@mapmycustomers/shared/types/entity";
import type { ActivityTypeRef } from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import type { FiWordCloudEntry } from "@mapmycustomers/shared/types/entity/fieldInsight";
import type DateRangeType from "@mapmycustomers/shared/types/range/DateRange";

import { DefaultDateRangeConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

export type WordCloudEntry = FiWordCloudEntry;

// One activity note behind a word-cloud term. The cloud counts activities only.
export interface WordCloudNote {
  activityType?: ActivityTypeRef;
  completedAt: Activity["completedAt"];
  id: Activity["id"];
  name: Activity["name"];
  note: NonNullable<Activity["note"]>;
  // The record the note hangs off: account first, then contact, then deal.
  relatedEntity?: {
    id: AnyEntityId;
    name: string;
    type: EntityTypesSupportedByActivities;
  };
}

export const fetchWordCloudData = createAction("@dashboard/fetchWordCloudData")<{
  callback: (data: WordCloudEntry[]) => void;
  // Explicit start/end dates; when present they take priority over dateRange.
  customRange?: DateRangeType;
  dateRange: DefaultDateRangeConfiguration;
  failureCallback?: () => void;
  scope?: Scope;
}>();

// No dedicated endpoint exists, so the drill-down re-queries those activities.
export const fetchWordCloudNotes = createAction("@dashboard/fetchWordCloudNotes")<{
  activityIds?: Activity["id"][];
  callback: (notes: WordCloudNote[], hasMore: boolean) => void;
  customRange?: DateRangeType;
  dateRange: DefaultDateRangeConfiguration;
  failureCallback?: () => void;
  limit: number;
  // The forms the cloud counted, not just the ones spelled like the term itself.
  matchedForms: string[];
  offset: number;
  // Server-side, so search spans every page rather than the loaded ones.
  query?: string;
  scope?: Scope;
  // Forms of the wider entries that consumed this term where they overlapped it.
  widerForms: string[];
  word: string;
}>();

export const EXPORT_PAGE_SIZE = 20;

export const exportWordCloudNotes = createAction("@dashboard/exportWordCloudNotes")<{
  activityIds?: Activity["id"][];
  callback: (notes: WordCloudNote[]) => void;
  customRange?: DateRangeType;
  dateRange: DefaultDateRangeConfiguration;
  failureCallback?: () => void;
  matchedForms: string[];
  progressCallback: (fetchedCount: number) => void;
  scope?: Scope;
  widerForms: string[];
}>();

export const cancelWordCloudNotesExport = createAction("@dashboard/cancelWordCloudNotesExport")();

export type WordCloudActions = ActionType<
  | typeof cancelWordCloudNotesExport
  | typeof exportWordCloudNotes
  | typeof fetchWordCloudData
  | typeof fetchWordCloudNotes
>;
